import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Gift } from "lucide-react";
import Footer from "../components/Footer";

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header with Login Button */}
      <header className="absolute top-0 right-0 z-50 p-6">
        <Button 
          data-testid="login-btn-header"
          onClick={() => navigate('/login')} 
          className="bg-white/90 backdrop-blur-sm text-foreground hover:bg-white rounded-full px-6 py-3 font-bold transition-all shadow-lg"
        >
          Se connecter
        </Button>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/3ad275be-157a-40a8-940d-c19ad22ec61a/images/be019609761ea072d2256ca2d7ba5c35ecc70c6a29e95a3ac15a6911b959811b.png')" }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="mb-8 flex justify-center items-end gap-3">
            <Gift className="w-20 h-20 text-primary" strokeWidth={1.5} />
            <span className="font-heading text-6xl md:text-7xl font-normal text-primary leading-none">GiftMe</span>
          </div>
          
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tight leading-tight text-white mb-6">
            Arrêtez de faire semblant<br />d'aimer vos cadeaux
          </h1>
          
          <p className="text-lg md:text-xl leading-relaxed text-white/90 mb-12 max-w-2xl mx-auto">
            Vos proches ont du cœur, mais pas forcément de goût. Guidez-les gentiment pour leur bien et celui de votre étagère en listant vos vraies envies.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              data-testid="get-started-btn"
              onClick={() => navigate('/register')} 
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
            >
              Créer ma liste de rêves
            </Button>
            <Button 
              data-testid="demo-btn"
              onClick={() => navigate('/demo')} 
              variant="outline"
              className="bg-white/10 backdrop-blur-sm text-white border-white/30 hover:bg-white/20 rounded-full px-8 py-6 text-lg font-bold transition-all"
            >
              Voir la démo
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-center mb-16 whitespace-nowrap">
            Comment ça fonctionne ?
          </h2>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-muted/30 rounded-3xl p-8 border border-white/50 backdrop-blur-sm text-center" data-testid="feature-create">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6 mx-auto">
                <span className="text-3xl">📝</span>
              </div>
              <h3 className="font-heading text-2xl font-bold mb-4">Crée ta liste de rêves</h3>
              <p className="leading-relaxed text-muted-foreground">
                Fini les "oh c'est gentil..." forcés ! Liste ce qui te fait vraiment kiffer avec le niveau d'envie : petit plaisir ou rêve ultime 🔥
              </p>
            </div>
            
            <div className="bg-muted/30 rounded-3xl p-8 border border-white/50 backdrop-blur-sm text-center" data-testid="feature-share">
              <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mb-6 mx-auto">
                <span className="text-3xl">🔗</span>
              </div>
              <h3 className="font-heading text-2xl font-bold mb-4">Partage le lien magique</h3>
              <p className="leading-relaxed text-muted-foreground">
                Un lien, c'est tout ! Envoie-le à mamie, tonton, ou ton pote qui offre toujours des trucs bizarres. Ils vont enfin savoir ce que tu veux 🎯
              </p>
            </div>
            
            <div className="bg-muted/30 rounded-3xl p-8 border border-white/50 backdrop-blur-sm text-center" data-testid="feature-reserve">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mb-6 mx-auto">
                <span className="text-3xl">🤫</span>
              </div>
              <h3 className="font-heading text-2xl font-bold mb-4">La surprise reste totale</h3>
              <p className="leading-relaxed text-muted-foreground">
                Tes proches réservent en secret, tu ne vois rien ! Fini les "et sinon t'aurais besoin de quoi ?"... Le jour J, c'est la vraie surprise 🎁
              </p>
            </div>
            
            <div className="bg-muted/30 rounded-3xl p-8 border border-white/50 backdrop-blur-sm text-center" data-testid="feature-enjoy">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6 mx-auto">
                <span className="text-3xl">🎉</span>
              </div>
              <h3 className="font-heading text-2xl font-bold mb-4">Kiff garanti !</h3>
              <p className="leading-relaxed text-muted-foreground">
                Tu débales des cadeaux que t'as vraiment envie d'avoir. Zéro fake smile, que du bonheur. C'est ça la magie de GiftMe 😍
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-background to-muted/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight mb-6">
            Prêt à commencer ?
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mb-8">
            Créez votre première wishlist en quelques secondes
          </p>
          <Button 
            data-testid="cta-register-btn"
            onClick={() => navigate('/register')} 
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
          >
            Créer ma liste de rêves
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}